import React, { memo, useState, useEffect, useRef } from 'react';
import { Card } from './Card';
import { Card as CardType } from '../types/memory-game.types';
import { GameDifficulty } from '@/contexts/games/types';
import { GAME_CONFIG } from '../utils/game-config';

interface GameBoardProps {
  cards: CardType[];
  difficulty: GameDifficulty;
  highlightedCards: number[];
  onCardClick: (cardId: number) => void;
  canFlipCard: (cardId: number) => boolean;
}

/**
 * Game board component that renders the grid of cards
 * Supports portrait and landscape orientations
 */
export const GameBoard = memo<GameBoardProps>(({
  cards,
  difficulty,
  highlightedCards,
  onCardClick,
  canFlipCard
}) => {
  const config = GAME_CONFIG[difficulty];
  // Size cards from the ACTUAL play area (the parent box), not the viewport:
  // the fleet sidebar (desktop) and the mobile top bar + game header take space,
  // so vw/vh math over-sized the grid and clipped rows above/below. The wrapper
  // fills its parent; a ResizeObserver feeds its box back into the grid.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const gapRem = config.cols > 4 ? 0.125 : 0.25;
  const gapPx = gapRem * 16;
  const availW = Math.max(0, box.w - (config.cols - 1) * gapPx);
  const availH = Math.max(0, box.h - (config.rows - 1) * gapPx);
  const cardPx = box.w && box.h ? Math.floor(Math.min(availW / config.cols, availH / config.rows)) : 0;
  const cardSize = cardPx > 0 ? `${cardPx}px` : `min(calc(90vw / ${config.cols}), calc(70vh / ${config.rows}))`;

  return (
    <div ref={wrapRef} className="flex h-full w-full items-center justify-center" data-testid="memory-board-wrap">
    <div
      className={`game-board difficulty-${difficulty}`}
      style={{
        '--grid-cols': config.cols,
        '--grid-rows': config.rows,
        '--card-size': cardSize,
        display: 'grid',
        gridTemplateColumns: `repeat(${config.cols}, var(--card-size))`,
        gridTemplateRows: `repeat(${config.rows}, var(--card-size))`,
        gap: `${gapRem}rem`,
        justifyContent: 'center',
        alignContent: 'center'
      } as React.CSSProperties}
      role="grid"
      aria-label={`Memory game board, ${difficulty} difficulty, ${config.cols} by ${config.rows} grid`}
      data-testid="memory-game-board"
      data-board-difficulty={difficulty}
      data-board-cols={config.cols}
      data-board-rows={config.rows}
    >
      {cards.map((card, index) => (
        <div
          key={card.id}
          role="gridcell"
          data-testid={`memory-cell-${index}`}
          style={{
            gridColumn: (index % config.cols) + 1,
            gridRow: Math.floor(index / config.cols) + 1
          }}
        >
          <Card
            card={card}
            isHighlighted={highlightedCards.includes(card.id)}
            onClick={onCardClick}
            canFlip={canFlipCard(card.id)}
          />
        </div>
      ))}
    </div>
    </div>
  );
});

GameBoard.displayName = 'GameBoard';
